import { campaignApiGet, campaignApiPost } from '@/lib/api/campaign-api-client';
import type { CampaignApiEnvelope } from '../types/campaigns-api.types';
import type {
  CancelSendJobResponseApi,
  CancelSendJobResponseEntityResponseApi,
  SendJobApi,
  SendJobEntityResponseApi,
  SendJobProgressApi,
  SendJobProgressResponseEntityResponseApi,
  SendJobSkipCountApi,
  SendJobSummaryApi,
  SendJobSummaryPagedResponseEntityResponseApi,
} from '../types/send-jobs-api.types';
import type {
  CancelSendJobResult,
  GetSendJobsParams,
  SendJob,
  SendJobProgress,
  SendJobsListResult,
} from '../types/send-jobs.types';

const assertSendJobApiSuccess = <T>(
  response: CampaignApiEnvelope<T> | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  if (response.data === undefined || response.data === null) {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return response.data;
};

const normalizeText = (value: string | null | undefined, fallback: string) =>
  value?.trim() || fallback;

const normalizeStatus = (value: string | null | undefined) =>
  normalizeText(value, 'queued').toLowerCase();

const mapSkipReasons = (
  reasons: SendJobSkipCountApi[] | null | undefined
): SendJob['skipReasons'] =>
  (reasons ?? [])
    .map((item) => ({
      reason: normalizeText(item.reason, 'Skipped'),
      count: item.count ?? 0,
    }))
    .filter((item) => item.count > 0);

const mapSendJob = (data: SendJobApi | SendJobSummaryApi): SendJob => {
  const summary = data as SendJobSummaryApi;
  const campaignId = data.campaignId?.trim() || null;

  return {
    id: normalizeText(data.itemId, ''),
    campaignId,
    campaignName: normalizeText(summary.campaignName, campaignId || 'Send job'),
    mode: normalizeText(data.mode, 'immediate'),
    status: normalizeStatus(data.status),
    scheduledAt: data.scheduledAt ?? null,
    startedAt: data.startedAt ?? null,
    completedAt: data.completedAt ?? null,
    createdAt: summary.createdDate ?? null,
    throttlePerHour: data.throttlePerHour ?? null,
    recipientCount: data.recipientCount ?? 0,
    processedCount: data.processedCount ?? 0,
    sentCount: data.sentCount ?? 0,
    failedCount: data.failedCount ?? 0,
    skippedCount: data.skippedCount ?? 0,
    skipReasons: mapSkipReasons(data.skipReasons),
    lastError: data.lastError?.trim() || null,
  };
};

const mapSendJobProgress = (data: SendJobProgressApi): SendJobProgress => ({
  status: normalizeStatus(data.status),
  recipientCount: data.recipientCount ?? 0,
  processedCount: data.processedCount ?? 0,
  sentCount: data.sentCount ?? 0,
  failedCount: data.failedCount ?? 0,
  skippedCount: data.skippedCount ?? 0,
  skipReasons: mapSkipReasons(data.skipReasons),
  throughputPerMinute: data.throughputPerMinute ?? 0,
  startedAt: data.startedAt ?? null,
  estimatedCompletionAt: data.estimatedCompletionAt ?? null,
  lastError: data.lastError?.trim() || null,
  queuedForSeconds: data.queuedForSeconds ?? 0,
  dispatcherOnline: data.dispatcherOnline ?? null,
});

const mapCancelSendJob = (data: CancelSendJobResponseApi): CancelSendJobResult => ({
  cancelled: Boolean(data.cancelled),
  alreadySent: data.alreadySent ?? 0,
});

export const getSendJobs = async (
  params: GetSendJobsParams = {}
): Promise<SendJobsListResult> => {
  const limit = params.limit ?? 10;
  const offset = params.offset ?? 0;
  const response = await campaignApiGet<SendJobSummaryPagedResponseEntityResponseApi>(
    '/v1/send-jobs',
    {
      Limit: limit,
      Offset: offset,
      Sort: params.sort ?? 'createdDate',
      Order: params.order ?? 'desc',
      CampaignId: params.campaignId,
      Status: params.status,
      Mode: params.mode,
    }
  );
  const data = assertSendJobApiSuccess(response, 'Failed to fetch send jobs');
  const items = (data.items ?? []).map(mapSendJob).filter((item) => item.id);

  return {
    items,
    totalCount: data.pageInfo?.total ?? items.length,
    hasNextPage: data.pageInfo?.hasNext ?? false,
    hasPreviousPage: data.pageInfo?.hasPrev ?? false,
  };
};

export const getSendJobById = async (itemId: string): Promise<SendJob> => {
  const id = itemId.trim();
  if (!id) {
    throw new Error('Send job id is required');
  }

  const response = await campaignApiGet<SendJobEntityResponseApi>(
    `/v1/send-jobs/${encodeURIComponent(id)}`
  );
  return mapSendJob(assertSendJobApiSuccess(response, 'Failed to fetch send job'));
};

export const getSendJobProgress = async (itemId: string): Promise<SendJobProgress> => {
  const id = itemId.trim();
  if (!id) {
    throw new Error('Send job id is required');
  }

  const response = await campaignApiGet<SendJobProgressResponseEntityResponseApi>(
    `/v1/send-jobs/${encodeURIComponent(id)}/progress`
  );
  return mapSendJobProgress(assertSendJobApiSuccess(response, 'Failed to fetch send progress'));
};

export const pauseSendJob = async (itemId: string): Promise<SendJob> => {
  const id = itemId.trim();
  if (!id) {
    throw new Error('Send job id is required');
  }

  const response = await campaignApiPost<SendJobEntityResponseApi>(
    `/v1/send-jobs/${encodeURIComponent(id)}/pause`,
    {}
  );
  return mapSendJob(assertSendJobApiSuccess(response, 'Failed to pause send job'));
};

export const resumeSendJob = async (itemId: string): Promise<SendJob> => {
  const id = itemId.trim();
  if (!id) {
    throw new Error('Send job id is required');
  }

  const response = await campaignApiPost<SendJobEntityResponseApi>(
    `/v1/send-jobs/${encodeURIComponent(id)}/resume`,
    {}
  );
  return mapSendJob(assertSendJobApiSuccess(response, 'Failed to resume send job'));
};

export const cancelSendJob = async (itemId: string): Promise<CancelSendJobResult> => {
  const id = itemId.trim();
  if (!id) {
    throw new Error('Send job id is required');
  }

  const response = await campaignApiPost<CancelSendJobResponseEntityResponseApi>(
    `/v1/send-jobs/${encodeURIComponent(id)}/cancel`,
    {}
  );
  return mapCancelSendJob(assertSendJobApiSuccess(response, 'Failed to cancel send job'));
};
